import React, { useState, useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import { IMediaFile } from 'components/Design/UploadsTab/Types'
import { FieldSchema } from 'components/Wizards/Wizard/Form/Types'
import { MediaPicker } from '../../../../MobileMediaPicker/MediaPicker'
import { Button } from 'blaze-ui/Button'
import { File02 } from 'blaze-ui/icons'
import { getMimeTypesForMediaTypes } from '../components/StartWithMedia/utils'
import { SelectedImagesPreview } from '../../../../MobileMediaPicker/SelectedImagesPreview'
import { ImageReviewDrawer } from '../../../../MobileMediaPicker/ImageReviewDrawer'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { useRootStore } from 'stores/RootStore'
import { observable } from 'mobx'
import { observer } from 'mobx-react-lite'
import Styles from './MediaPickerField.module.scss'
import { MultiStepSchema } from 'components/Wizards/schemas/Types'

export const MediaPickerField = observer(({ name, schema }: { name: string; schema: FieldSchema }) => {
  const [, , helpers] = useField(name)
  const { submitForm } = useFormikContext()
  const { skipBackgroundRemoval, supportedMediaTypes = ['image'], multiSelect = false } = schema.describe().meta || {}

  const wizardSchema = useWizardSchema() as MultiStepSchema
  const { submitLabel = 'Generate Post' } = wizardSchema.meta

  const { drawersStore } = useRootStore()
  const { openDrawer, closeDrawer } = drawersStore
  const accept = getMimeTypesForMediaTypes(supportedMediaTypes)

  const [selectedFiles] = React.useState(() => observable.array<IMediaFile>([]))
  const [previewVisible, setPreviewVisible] = useState(false)

  // Update preview visibility state when selectedFiles changes
  useEffect(() => {
    setPreviewVisible(selectedFiles.length > 0 && multiSelect)
  }, [selectedFiles.length, multiSelect])

  const onSelect = (file: IMediaFile) => {
    if (!multiSelect) {
      helpers.setValue(file.source_url)
    } else {
      if (selectedFiles.some((f) => f.source_url === file.source_url)) {
        const newSelectedFiles = selectedFiles.filter((f) => f.source_url !== file.source_url)
        selectedFiles.replace(newSelectedFiles)
      } else {
        selectedFiles.push(file)
      }
      helpers.setValue(selectedFiles.map((f) => f.source_url))
    }
  }

  const finalizeSelection = () => {
    openDrawer(ImageReviewDrawer, {
      onClose: () => {
        closeDrawer()
      },
      onSubmit: submitForm,
      selectedFiles,
      submitLabel,
    })
  }

  const clearSelection = () => {
    selectedFiles.clear()
    if (!multiSelect) {
      helpers.setValue(undefined)
    } else {
      helpers.setValue([])
    }
  }

  return (
    <div className={previewVisible ? Styles.containerWithPreview : Styles.container}>
      <MediaPicker
        onSelect={onSelect}
        skipBackgroundRemoval={skipBackgroundRemoval}
        altButton={
          <Button size="lg" variant="tertiary" frontIcon={File02} fullWidth onPress={submitForm}>
            I just need text
          </Button>
        }
        accept={accept}
        multiSelect={multiSelect}
        selectedFiles={selectedFiles}
      />

      {multiSelect && (
        <SelectedImagesPreview
          selectedFiles={selectedFiles}
          onReviewImages={finalizeSelection}
          onClear={clearSelection}
          onSubmit={submitForm}
          submitLabel={submitLabel}
        />
      )}
    </div>
  )
})
